import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { DialogTopBar } from '../components/DialogTopBar';
import { toast } from 'sonner';
import { Plus, UserCog, Edit, Trash2, KeyRound, Loader2, Eye, EyeOff, MoreHorizontal } from 'lucide-react';

const UsersPage = () => {
    const { getAuthHeaders, API, isAdmin, user: currentUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [passwordModalOpen, setPasswordModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [resetUser, setResetUser] = useState(null);
    const [saving, setSaving] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);

    const [formData, setFormData] = useState({
        username: '',
        name: '',
        email: '',
        password: '',
        role: 'lecturer'
    });

    const [newPassword, setNewPassword] = useState('');

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const response = await axios.get(`${API}/users`, { headers: getAuthHeaders() });
            setUsers(response.data);
        } catch (error) {
            console.error('Failed to fetch users:', error);
            toast.error('Failed to load users');
        } finally {
            setLoading(false);
        }
    };

    const openModal = (user = null) => {
        if (user) {
            setEditingUser(user);
            setFormData({
                username: user.username,
                name: user.name,
                email: user.email || '',
                password: '',
                role: user.role
            });
        } else {
            setEditingUser(null);
            setFormData({ username: '', name: '', email: '', password: '', role: 'lecturer' });
        }
        setShowPassword(false);
        setModalOpen(true);
    };

    const openPasswordModal = (user) => {
        setResetUser(user);
        setNewPassword('');
        setShowNewPassword(false);
        setPasswordModalOpen(true);
    };

    const handleSubmit = async () => {
        if (!formData.username || !formData.name) {
            toast.error('Please fill in required fields');
            return;
        }

        if (!editingUser && !formData.password) {
            toast.error('Password is required for new users');
            return;
        }

        setSaving(true);
        try {
            if (editingUser) {
                await axios.put(`${API}/users/${editingUser.id}`, {
                    name: formData.name,
                    email: formData.email || null,
                    role: formData.role
                }, { headers: getAuthHeaders() });
                toast.success('User updated successfully');
            } else {
                await axios.post(`${API}/users`, formData, { headers: getAuthHeaders() });
                toast.success('User created successfully');
            }
            
            setModalOpen(false);
            fetchUsers();
        } catch (error) {
            console.error('Failed to save user:', error);
            toast.error(error.response?.data?.detail || 'Failed to save user');
        } finally {
            setSaving(false);
        }
    };

    const handleResetPassword = async () => {
        if (!newPassword) {
            toast.error('Please enter a new password');
            return;
        }

        if (newPassword.length < 6) {
            toast.error('Password must be at least 6 characters');
            return;
        }

        setSaving(true);
        try {
            await axios.post(`${API}/users/${resetUser.id}/reset-password`, {
                new_password: newPassword
            }, { headers: getAuthHeaders() });
            
            toast.success('Password reset successfully');
            setPasswordModalOpen(false);
        } catch (error) {
            console.error('Failed to reset password:', error);
            toast.error('Failed to reset password');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (user) => {
        if (user.id === currentUser.id) {
            toast.error("You cannot delete your own account");
            return;
        }

        if (!window.confirm(`Are you sure you want to delete "${user.name}"?`)) return;
        
        try {
            await axios.delete(`${API}/users/${user.id}`, { headers: getAuthHeaders() });
            toast.success('User deleted successfully');
            fetchUsers();
        } catch (error) {
            console.error('Failed to delete user:', error);
            toast.error(error.response?.data?.detail || 'Failed to delete user');
        }
    };

    if (!isAdmin()) {
        return (
            <Layout>
                <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
                    <UserCog className="w-10 h-10 mx-auto mb-3 text-gray-400 opacity-40" />
                    <h3 className="text-base font-semibold text-gray-700 mb-1">Admin Access Required</h3>
                    <p className="text-gray-500">Only administrators can manage users</p>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6 animate-fade-in" data-testid="users-page">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="font-heading text-3xl font-bold text-ica-navy">User Management</h1>
                        <p className="text-gray-500 mt-1">Manage lecturers and administrators</p>
                    </div>
                    <Button 
                        onClick={() => openModal()}
                        data-testid="add-user-btn"
                        
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Add User
                    </Button>
                </div>

                {/* Users Table */}
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    {loading ? (
                        <div className="p-8 text-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ica-navy mx-auto" />
                        </div>
                    ) : users.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="w-full ica-table">
                                <thead>
                                    <tr>
                                        <th className="px-6 py-4 text-left">User</th>
                                        <th className="px-6 py-4 text-left">Username</th>
                                        <th className="px-6 py-4 text-left">Email</th>
                                        <th className="px-6 py-4 text-center">Role</th>
                                        <th className="px-6 py-4 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.map((user) => (
                                        <tr key={user.id} className="border-b border-gray-100 hover:bg-ica-gold/5 transition-colors" data-testid={`user-row-${user.id}`}>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                                        user.role === 'admin' ? 'bg-ica-gold/20' : 'bg-ica-navy/10'
                                                    }`}>
                                                        <UserCog className={`w-5 h-5 ${
                                                            user.role === 'admin' ? 'text-ica-gold' : 'text-ica-navy'
                                                        }`} />
                                                    </div>
                                                    <span className="font-medium text-gray-900">{user.name}</span>
                                                    {user.id === currentUser.id && (
                                                        <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">You</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-gray-600 font-mono text-sm">{user.username}</td>
                                            <td className="px-6 py-4 text-gray-500">{user.email || '-'}</td>
                                            <td className="px-6 py-4 text-center">
                                                <span className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${
                                                    user.role === 'admin' 
                                                        ? 'bg-ica-gold/10 text-ica-gold-dark' 
                                                        : 'bg-ica-navy/10 text-ica-navy'
                                                }`}>
                                                    {user.role}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button variant="outline" size="sm" data-testid={`user-actions-${user.id}`} title="Actions">
                                                            <MoreHorizontal className="w-3.5 h-3.5" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end">
                                                        <DropdownMenuItem onClick={() => openPasswordModal(user)} data-testid={`reset-password-${user.id}`}>
                                                            <KeyRound className="w-3.5 h-3.5 mr-2" /> Reset password
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem onClick={() => openModal(user)} data-testid={`edit-user-${user.id}`}>
                                                            <Edit className="w-3.5 h-3.5 mr-2" /> Edit user
                                                        </DropdownMenuItem>
                                                        {user.id !== currentUser.id && (
                                                            <DropdownMenuItem onClick={() => handleDelete(user)} className="text-red-600 focus:text-red-600" data-testid={`delete-user-${user.id}`}>
                                                                <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete user
                                                            </DropdownMenuItem>
                                                        )}
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-center py-10">
                            <UserCog className="w-10 h-10 mx-auto mb-3 text-gray-400 opacity-40" />
                            <h3 className="text-base font-semibold text-gray-700 mb-1">No users found</h3>
                            <p className="text-gray-500 mb-4">Add lecturers and administrators</p>
                            <Button onClick={() => openModal()} >
                                <Plus className="w-4 h-4 mr-2" />
                                Add User
                            </Button>
                        </div>
                    )}
                </div>

                {/* Add/Edit Modal */}
                <Dialog open={modalOpen} onOpenChange={setModalOpen}>
                    <DialogContent data-testid="user-modal">
                        <DialogTopBar title={<>{editingUser ? 'Edit User' : 'Add New User'}</>}>
    <Button size="sm" variant="outline" onClick={() => setModalOpen(false)}>
                                Cancel
                            </Button>
                            <Button size="sm" 
                                onClick={handleSubmit}
                                disabled={saving}
                                data-testid="save-user-btn"
                                
                            >
                                {saving ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    editingUser ? 'Update User' : 'Create User'
                                )}
                            </Button>
</DialogTopBar>
                        
                        <div className="space-y-4 py-4">
                            <div className="space-y-2">
                                <Label>Username *</Label>
                                <Input
                                    data-testid="user-username-input"
                                    value={formData.username}
                                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                                    placeholder="Enter username"
                                    disabled={!!editingUser}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label>Full Name *</Label>
                                <Input
                                    data-testid="user-name-input"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder="Enter full name"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label>Email</Label>
                                <Input
                                    data-testid="user-email-input"
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    placeholder="user@example.com"
                                />
                            </div>

                            {!editingUser && (
                                <div className="space-y-2">
                                    <Label>Password *</Label>
                                    <div className="relative">
                                        <Input
                                            data-testid="user-password-input"
                                            type={showPassword ? 'text' : 'password'}
                                            value={formData.password}
                                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                            placeholder="Enter password"
                                            className="pr-10"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        >
                                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div className="space-y-2">
                                <Label>Role *</Label>
                                <Select 
                                    value={formData.role} 
                                    onValueChange={(value) => setFormData({ ...formData, role: value })}
                                >
                                    <SelectTrigger data-testid="user-role-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="lecturer">Lecturer</SelectItem>
                                        <SelectItem value="admin">Admin</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>

                {/* Password Reset Modal */}
                <Dialog open={passwordModalOpen} onOpenChange={setPasswordModalOpen}>
                    <DialogContent data-testid="password-modal">
                        <DialogTopBar title={<>Reset Password</>}>
    <Button size="sm" variant="outline" onClick={() => setPasswordModalOpen(false)}>
                                Cancel
                            </Button>
                            <Button size="sm" 
                                onClick={handleResetPassword}
                                disabled={saving}
                                data-testid="confirm-reset-password-btn"
                                
                            >
                                {saving ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Resetting...
                                    </>
                                ) : (
                                    'Reset Password'
                                )}
                            </Button>
</DialogTopBar>
                        
                        <div className="py-4">
                            <p className="text-sm text-gray-500 mb-4">
                                Reset password for <strong>{resetUser?.name}</strong>
                            </p>
                            <div className="space-y-2">
                                <Label>New Password</Label>
                                <div className="relative">
                                    <Input
                                        data-testid="new-password-input"
                                        type={showNewPassword ? 'text' : 'password'}
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        placeholder="Enter new password"
                                        className="pr-10"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowNewPassword(!showNewPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    >
                                        {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
};

export default UsersPage;
